import { useEffect, useState } from 'react'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

/**
 * Platform Stats — super-admin, presentation-friendly "user stats" for
 * partners (e.g. Golf in Ontario). Live courses by provider + region,
 * registered golfers, and bookings + GMV. Course metrics are shown
 * platform-wide AND for Ontario side by side; users/bookings are
 * platform-wide (not province-tagged in the schema). Screenshot each
 * card straight into a deck.
 *
 * Backed by GET /api/dashboard/user-stats.
 */
export default function PlatformStats() {
  const [tab, setTab] = useState('coverage')
  return (
    <AdminShell title="Platform Stats">
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid #e2e8f0' }}>
        <TabBtn active={tab === 'coverage'} onClick={() => setTab('coverage')}>Coverage</TabBtn>
        <TabBtn active={tab === 'engagement'} onClick={() => setTab('engagement')}>Engagement</TabBtn>
      </div>
      {tab === 'coverage' ? <Coverage /> : <Engagement />}
    </AdminShell>
  )
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
      fontSize: 14, fontWeight: active ? 700 : 500, color: active ? '#0f172a' : '#64748b',
      borderBottom: active ? '3px solid #00d19f' : '3px solid transparent', marginBottom: -1,
    }}>{children}</button>
  )
}

function Coverage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true); setError(null)
    tournamentApi.get('/dashboard/user-stats')
      .then(r => setData(r.data))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [])

  return (
    <>
      <p style={{ color: '#64748b', margin: '0 0 20px', maxWidth: 820, lineHeight: 1.5 }}>
        Course coverage + totals for partner presentations. Courses are shown
        <b> platform-wide</b> and for <b>Ontario</b> side by side; golfer sign-ups and
        bookings are platform-wide. Each card is screenshot-ready.
      </p>
      {loading ? (
        <div style={empty}>Loading stats…</div>
      ) : error ? (
        <div style={{ ...empty, color: '#b91c1c', borderColor: '#fecaca', background: '#fef2f2' }}>{error}</div>
      ) : !data ? (
        <div style={empty}>No data.</div>
      ) : (
        <Stats data={data} />
      )}
    </>
  )
}

const DAY_OPTS = [{ v: 7, l: '7 days' }, { v: 30, l: '30 days' }, { v: 90, l: '90 days' }, { v: 365, l: '12 months' }]

function Engagement() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [resellerId, setResellerId] = useState('')
  const [days, setDays] = useState(30)

  useEffect(() => {
    setLoading(true); setError(null)
    const qs = new URLSearchParams({ days: String(days) })
    if (resellerId) qs.set('resellerId', resellerId)
    tournamentApi.get(`/dashboard/engagement?${qs.toString()}`)
      .then(r => setData(r.data))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [resellerId, days])

  const resellers = data?.resellers || []
  const c = data?.consumer || {}
  const p = data?.partner || {}

  return (
    <>
      <p style={{ color: '#64748b', margin: '0 0 16px', maxWidth: 820, lineHeight: 1.5 }}>
        What users are doing and building — custom-trip + booking funnels, portal
        activity, and embed traffic. Filter by reseller and time window.
      </p>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
        <label style={ctrlLabel}>Reseller</label>
        <select value={resellerId} onChange={e => setResellerId(e.target.value)} style={select}>
          <option value="">All resellers (platform)</option>
          {resellers.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <label style={{ ...ctrlLabel, marginLeft: 8 }}>Window</label>
        <div style={{ display: 'flex', gap: 4 }}>
          {DAY_OPTS.map(o => (
            <button key={o.v} onClick={() => setDays(o.v)} style={{
              padding: '7px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
              border: '1px solid ' + (days === o.v ? '#00d19f' : '#cbd5e1'),
              background: days === o.v ? '#e6fbf4' : '#fff',
              color: days === o.v ? '#0f766e' : '#334155', fontWeight: days === o.v ? 700 : 500,
            }}>{o.l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={empty}>Loading engagement…</div>
      ) : error ? (
        <div style={{ ...empty, color: '#b91c1c', borderColor: '#fecaca', background: '#fef2f2' }}>{error}</div>
      ) : !data ? (
        <div style={empty}>No data.</div>
      ) : (
        <div style={{ maxWidth: 1040 }}>
          {/* Headline KPIs */}
          <div style={kpiGrid}>
            <Kpi label="Custom trips started" value={fmtInt(c.quotes?.total)} sub={`in last ${data.days} days`} accent="#00d19f" />
            <Kpi label="Bookings" value={fmtInt(c.bookings?.total)} sub={fmtMoney(c.bookings?.gmv) + ' GMV'} accent="#8b5cf6" />
            <Kpi label="Engaged customers" value={fmtInt(c.engagedCustomers)} sub="with a trip or booking" accent="#3b82f6" />
            <Kpi label="Embed views" value={c.embedTracked ? fmtInt(c.embedViews) : '—'} sub={c.embedTracked ? `${fmtInt(c.embedSearches)} searches` : 'tracking starts at deploy'} accent="#f59e0b" />
          </div>

          <SectionTitle>Embed conversion funnel <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8' }}>(how far visitors get in the widget)</span></SectionTitle>
          <Card title={`Steps a visitor reaches (last ${data.days} days)`}>
            <EmbedFunnel funnel={c.embedFunnel} tracked={c.embedTracked} />
          </Card>

          <SectionTitle>Consumer activity</SectionTitle>
          <div style={twoCol}>
            <Card title="Custom trips by stage">
              <FunnelBars rows={(c.quotes?.byStatus || []).map(r => [prettyStatus(r.status), r.count])} />
            </Card>
            <Card title="Bookings by stage">
              <FunnelBars rows={(c.bookings?.byStatus || []).map(r => [prettyStatus(r.status), r.count])} />
            </Card>
          </div>
          <Card title="Custom-trip activity (lifecycle events)">
            <SimpleTable
              head={['Event', 'Count']}
              rows={(c.quoteEvents || []).map(r => [prettyStatus(r.type), fmtInt(r.count)])}
              maxHeight={240}
            />
          </Card>

          <SectionTitle>Partner activity (in the portal)</SectionTitle>
          <div style={twoCol}>
            <Card title="Team + logins">
              <MiniRow label="Active team members" value={fmtInt(p.activeTeam)} strong />
              <MiniRow label={`Operators who logged in (${data.days}d)`} value={fmtInt(p.operatorsLoggedIn)} />
            </Card>
            <Card title="Things they built / connected">
              <MiniRow label={`Packages built (${data.days}d)`} value={fmtInt(p.packagesBuilt)} />
              <MiniRow label={`Embeds / groupings built (${data.days}d)`} value={fmtInt(p.groupingsBuilt)} />
              <MiniRow label="Courses connected (current)" value={fmtInt(p.coursesConnected)} strong />
              <MiniRow label={`Courses connected (new, ${data.days}d)`} value={fmtInt(p.coursesConnectedNew)} />
            </Card>
          </div>

          <SectionTitle>Stay &amp; Play widget funnel <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8' }}>(platform-wide)</span></SectionTitle>
          <Card title={`Widget events (last ${data.days} days)`}>
            <SimpleTable
              head={['Step', 'Count']}
              rows={(data.widgetFunnel || []).map(r => [prettyStatus(r.event), fmtInt(r.count)])}
            />
          </Card>

          <div style={{ marginTop: 20, fontSize: 12, color: '#94a3b8', lineHeight: 1.6, maxWidth: 820 }}>
            {(data.notes || []).map((n, i) => <div key={i}>• {n}</div>)}
            {data.generatedAt && <div style={{ marginTop: 8 }}>Generated {new Date(data.generatedAt).toLocaleString()}</div>}
          </div>
        </div>
      )}
    </>
  )
}

function FunnelBars({ rows }) {
  const max = Math.max(1, ...rows.map(r => Number(r[1]) || 0))
  if (!rows.length) return <div style={{ color: '#94a3b8', fontSize: 13, padding: '8px 0' }}>No activity in this window.</div>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map(([label, val], i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 150, fontSize: 12, color: '#475569', flexShrink: 0, textAlign: 'right' }}>{label}</div>
          <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 6, height: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ width: `${(Number(val) / max) * 100}%`, background: '#00d19f', height: '100%', borderRadius: 6, minWidth: 2 }} />
          </div>
          <div style={{ width: 44, fontSize: 13, fontWeight: 700, color: '#0f172a', textAlign: 'right', flexShrink: 0 }}>{fmtInt(val)}</div>
        </div>
      ))}
    </div>
  )
}

// Embed conversion funnel — each stage is distinct SESSIONS that reached it,
// so the step-to-step drop reveals where visitors fall out. Bars are scaled
// to the top stage (Opened) so the taper is the story. The "empty searches"
// callout separates a supply/coverage gap from a price/UX drop-off.
function EmbedFunnel({ funnel, tracked }) {
  if (!tracked) {
    return <div style={{ color: '#94a3b8', fontSize: 13, padding: '8px 0' }}>Embed funnel tracking is not live yet — counts start accruing once instrumentation deploys.</div>
  }
  const stages = funnel?.stages || []
  const top = Math.max(1, Number(stages[0]?.sessions) || 0)
  if (!stages.length || top <= 0) {
    return <div style={{ color: '#94a3b8', fontSize: 13, padding: '8px 0' }}>No embed activity in this window.</div>
  }
  const nr = funnel?.noResults || {}
  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {stages.map((st, i) => {
          const val = Number(st.sessions) || 0
          const prev = i > 0 ? (Number(stages[i - 1]?.sessions) || 0) : null
          const pctOfTop = Math.round((val / top) * 100)
          // Step conversion from the previous stage — the drop-off tell.
          const stepPct = prev != null && prev > 0 ? Math.round((val / prev) * 100) : null
          const dropped = prev != null ? Math.max(0, prev - val) : 0
          return (
            <div key={st.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 150, fontSize: 12, color: '#475569', flexShrink: 0, textAlign: 'right' }}>{st.label}</div>
              <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 6, height: 24, position: 'relative', overflow: 'hidden' }}>
                <div style={{ width: `${pctOfTop}%`, background: '#00d19f', height: '100%', borderRadius: 6, minWidth: val > 0 ? 2 : 0 }} />
                <div style={{ position: 'absolute', top: 0, left: 8, height: '100%', display: 'flex', alignItems: 'center', fontSize: 11, color: '#334155' }}>{pctOfTop}% of opened</div>
              </div>
              <div style={{ width: 56, fontSize: 14, fontWeight: 700, color: '#0f172a', textAlign: 'right', flexShrink: 0 }}>{fmtInt(val)}</div>
              <div style={{ width: 92, fontSize: 11, color: stepPct != null && stepPct < 50 ? '#b91c1c' : '#94a3b8', flexShrink: 0, textAlign: 'right' }}>
                {stepPct != null ? `${stepPct}% kept${dropped ? ` · −${fmtInt(dropped)}` : ''}` : ''}
              </div>
            </div>
          )
        })}
      </div>
      {/* Supply/coverage tell: searches that returned nothing. */}
      {nr.emptyRate != null && (
        <div style={{
          marginTop: 14, padding: '10px 14px', borderRadius: 8,
          background: nr.emptyRate >= 25 ? '#fef2f2' : '#f8fafc',
          border: '1px solid ' + (nr.emptyRate >= 25 ? '#fecaca' : '#e2e8f0'),
          fontSize: 13, color: '#334155', lineHeight: 1.5,
        }}>
          <strong style={{ color: nr.emptyRate >= 25 ? '#b91c1c' : '#0f172a' }}>{nr.emptyRate}% of searches came back empty</strong>
          {' '}— {fmtInt(nr.sessions)} session{nr.sessions === 1 ? '' : 's'} searched and saw nothing available.
          {nr.emptyRate >= 25
            ? ' A high empty rate points to a coverage/supply gap (no hotels or rounds in the area), not a price/UX drop-off.'
            : ' Most searches are returning results, so drop-off below is more likely price or UX than supply.'}
        </div>
      )}
    </div>
  )
}

function prettyStatus(s) {
  if (!s) return '—'
  return String(s).replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())
}

function Stats({ data }) {
  const c = data.courses || {}
  const plat = c.platform || {}
  const on = c.ontario || {}
  const u = data.users || {}
  const b = data.bookings || {}

  return (
    <div style={{ maxWidth: 1040 }}>
      {/* Headline KPI row */}
      <div style={kpiGrid}>
        <Kpi label="Live courses" value={fmtInt(plat.live)} sub={`${fmtInt(plat.total)} in catalog`} accent="#00d19f" />
        <Kpi label="Registered golfers" value={fmtInt(u.total)} sub={`${fmtInt(u.new30)} new in 30d`} accent="#3b82f6" />
        <Kpi label="Bookings" value={fmtInt(b.combinedCount)} sub={`${fmtInt(b.reseller?.count30 + (b.playStay?.count30 || 0))} in 30d`} accent="#8b5cf6" />
        <Kpi label="Total booking value" value={fmtMoney(b.combinedGmv)} sub="customer paid (GMV)" accent="#f59e0b" />
      </div>

      {/* ── COURSES ─────────────────────────────────────────────── */}
      <SectionTitle>Courses</SectionTitle>
      <div style={twoCol}>
        <Card title="Platform">
          <MiniRow label="In catalog (active)" value={fmtInt(plat.active)} />
          <MiniRow label="Live / bookable" value={fmtInt(plat.live)} strong />
          <MiniRow label="On the map (geo-located)" value={fmtInt(plat.mappable)} />
        </Card>
        <Card title="Ontario">
          <MiniRow label="Total courses" value={fmtInt(on.total)} />
          <MiniRow label="Live / bookable" value={fmtInt(on.live)} strong />
          <MiniRow label="On the map (geo-located)" value={fmtInt(on.mappable)} />
        </Card>
      </div>

      <div style={twoCol}>
        <Card title="By provider (platform)">
          <SimpleTable
            head={['Provider', 'Courses']}
            rows={(plat.byProvider || []).map(r => [prettyProvider(r.provider), fmtInt(r.count)])}
          />
        </Card>
        <Card title="By province">
          <SimpleTable
            head={['Province', 'Courses', 'Live']}
            rows={(plat.byProvince || []).map(r => [
              r.province + (r.iso ? ` (${r.iso})` : ''), fmtInt(r.count), fmtInt(r.live),
            ])}
          />
        </Card>
      </div>

      <Card title="Ontario courses by city">
        <SimpleTable
          head={['City', 'Courses']}
          rows={(on.byCity || []).map(r => [r.city, fmtInt(r.count)])}
          maxHeight={280}
        />
      </Card>

      {/* ── GOLFERS ─────────────────────────────────────────────── */}
      <SectionTitle>Registered golfers (platform-wide)</SectionTitle>
      <div style={twoCol}>
        <Card title="Sign-ups">
          <MiniRow label="Total accounts" value={fmtInt(u.total)} strong />
          <MiniRow label="New in last 30 days" value={fmtInt(u.new30)} />
          <MiniRow label="New in last 90 days" value={fmtInt(u.new90)} />
        </Card>
        <Card title="By product">
          <MiniRow label="Marketplace (custom trips / S&P)" value={fmtInt(u.resellerCustomers)} />
          <MiniRow label="Stay & Play engine" value={fmtInt(u.playStayCustomers)} />
        </Card>
      </div>

      {/* ── BOOKINGS ────────────────────────────────────────────── */}
      <SectionTitle>Bookings (platform-wide)</SectionTitle>
      <div style={twoCol}>
        <Card title="Marketplace bookings">
          <MiniRow label="Confirmed / completed trips" value={fmtInt(b.reseller?.count)} strong />
          <MiniRow label="Gross value (GMV)" value={fmtMoney(b.reseller?.gmv)} />
          <MiniRow label="Net to resellers" value={fmtMoney(b.reseller?.net)} />
          <MiniRow label="Trips in last 30 days" value={fmtInt(b.reseller?.count30)} />
          <MiniRow label="GMV in last 30 days" value={fmtMoney(b.reseller?.gmv30)} />
        </Card>
        <Card title="Stay & Play engine bookings">
          <MiniRow label="Bookings" value={fmtInt(b.playStay?.count)} strong />
          <MiniRow label="Gross value (GMV)" value={fmtMoney(b.playStay?.gmv)} />
          <MiniRow label="Bookings in last 30 days" value={fmtInt(b.playStay?.count30)} />
          <MiniRow label="GMV in last 30 days" value={fmtMoney(b.playStay?.gmv30)} />
        </Card>
      </div>

      {/* Footnotes */}
      <div style={{ marginTop: 20, fontSize: 12, color: '#94a3b8', lineHeight: 1.6, maxWidth: 820 }}>
        {(data.notes || []).map((n, i) => <div key={i}>• {n}</div>)}
        {data.generatedAt && (
          <div style={{ marginTop: 8 }}>Generated {new Date(data.generatedAt).toLocaleString()}</div>
        )}
      </div>
    </div>
  )
}

/* ── helpers + presentational bits ──────────────────────────────── */
function prettyProvider(p) {
  if (!p) return '—'
  const map = { 'teetime-com': 'TeeTime.com', 'teeon': 'Tee-On', 'lightspeed': 'Lightspeed', 'chronogolf': 'Chronogolf', 'not integrated': 'Not integrated' }
  return map[p] || p
}
function fmtInt(n) { return Number(n || 0).toLocaleString('en-US') }
function fmtMoney(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', borderTop: `3px solid ${accent}` }}>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: '#0f172a', margin: '6px 0 2px' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#94a3b8' }}>{sub}</div>
    </div>
  )
}
function Card({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  )
}
function MiniRow({ label, value, strong }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: 13, color: '#475569' }}>{label}</span>
      <span style={{ fontSize: strong ? 18 : 14, fontWeight: strong ? 800 : 600, color: '#0f172a' }}>{value}</span>
    </div>
  )
}
function SimpleTable({ head, rows, maxHeight }) {
  return (
    <div style={{ maxHeight, overflowY: maxHeight ? 'auto' : 'visible' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>{head.map((h, i) => (
            <th key={i} style={{ textAlign: i === 0 ? 'left' : 'right', color: '#64748b', fontWeight: 600, padding: '6px 8px', borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0, background: '#fff' }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} style={{ padding: '10px 8px', color: '#94a3b8' }}>No data.</td></tr>
          ) : rows.map((r, ri) => (
            <tr key={ri}>{r.map((cell, ci) => (
              <td key={ci} style={{ textAlign: ci === 0 ? 'left' : 'right', padding: '6px 8px', borderBottom: '1px solid #f1f5f9', color: '#0f172a' }}>{cell}</td>
            ))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
function SectionTitle({ children }) {
  return <h2 style={{ fontSize: 16, color: '#0f172a', margin: '28px 0 12px' }}>{children}</h2>
}

const empty = { padding: '40px', textAlign: 'center', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: 12, background: '#fff' }
const kpiGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 8 }
const twoCol = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }
const ctrlLabel = { fontSize: 12, color: '#64748b', fontWeight: 600 }
const select = { padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', color: '#0f172a', minWidth: 200 }
